import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { Shell, Quiet, Button, errText } from "./ui.jsx";
import Home from "./Home.jsx";
import SettingsHub from "./SettingsHub.jsx";
import Access from "./Access.jsx";
import Hiring, { AllApplicants } from "./Hiring.jsx";
import Applicant from "./Applicant.jsx";
import { DispatchOrOldLink, Drivers, Loads, Equipment } from "./DispatchPages.jsx";
import LoadPage from "./LoadPage.jsx";
import DriverPage from "./DriverPage.jsx";
import Rules from "./Rules.jsx";
import People from "./People.jsx";
import CallReports from "./CallReports.jsx";
import Savings from "./Savings.jsx";
import DoNotCall from "./DoNotCall.jsx";
import Billing from "./Billing.jsx";

/**
 * The carrier's own pages, under /carrier (docs/freight-carrier-pages-plan-2026-10.md). This reads
 * the carrier and who is signed in once and hands both to every page.
 *
 * ⛔ WHAT IS DRAWN IS NOT THE GATE. `me` decides which tiles and links appear; the server refuses
 * anything a person may not open, on every route, whatever this shows.
 */
export const CarrierCtx = createContext(null);
export const useCarrier = () => useContext(CarrierCtx);

export default function CarrierApp({ initial }) {
  const [state, setState] = useState(initial ? { carrier: initial, error: null } : { carrier: undefined, error: null });
  const reload = useCallback(async () => {
    try { setState({ carrier: await freight.getCarrier(), error: null }); }
    catch (e) { setState((s) => ({ carrier: s.carrier ?? null, error: errText(e) })); }
  }, []);
  useEffect(() => { if (!initial) reload(); }, [initial, reload]);

  const { carrier, error } = state;
  if (carrier === undefined && !error) return <Shell settings={false}><Quiet>Loading…</Quiet></Shell>;
  if (!carrier) {
    return (
      <Shell settings={false}>
        <Quiet>{error ? `Your account could not be read: ${error}` : "This sign-in has no carrier yet."}</Quiet>
        {error
          ? <div><Button kind="secondary" onClick={() => { setState({ carrier: undefined, error: null }); reload(); }}>Try again</Button></div>
          : <Link to="/carrier/setup" style={{ fontSize: 16, fontWeight: 500 }}>Set one up →</Link>}
      </Shell>
    );
  }
  return (
    <CarrierCtx.Provider value={{ carrier, me: carrier.me || {}, reload }}>
      <Routes>
        <Route index element={<Home />} />
        <Route path="hiring" element={<Hiring />} />
        <Route path="hiring/all" element={<AllApplicants />} />
        <Route path="hiring/:id" element={<Applicant />} />
        <Route path="dispatch" element={<DispatchOrOldLink />} />
        <Route path="dispatch/drivers" element={<Drivers />} />
        <Route path="dispatch/drivers/:id" element={<DriverPage />} />
        <Route path="dispatch/loads" element={<Loads />} />
        <Route path="dispatch/loads/:id" element={<LoadPage />} />
        <Route path="dispatch/trucks" element={<Equipment kind="trucks" />} />
        <Route path="dispatch/trailers" element={<Equipment kind="trailers" />} />
        <Route path="settings" element={<SettingsHub />} />
        <Route path="settings/access" element={<Access />} />
        <Route path="settings/rules" element={<Rules />} />
        <Route path="settings/people" element={<People />} />
        <Route path="settings/call-reports" element={<CallReports />} />
        <Route path="settings/savings" element={<Savings />} />
        <Route path="settings/do-not-call" element={<DoNotCall />} />
        <Route path="settings/billing" element={<Billing />} />
        <Route path="settings/*" element={<Navigate to="/carrier/settings" replace />} />
        <Route path="*" element={<Navigate to="/carrier" replace />} />
      </Routes>
    </CarrierCtx.Provider>
  );
}
